import { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';
import { RefreshCw, Clock, CheckCircle, XCircle } from 'lucide-react';

interface SyncQueueItem {
  id: string;
  variantId: string;
  targetShopIds: string[];
  status: 'pending' | 'processing' | 'done' | 'failed';
  stockToSync: number;
  attempts: number;
  errorMessage: string | null;
  createdAt: string;
}

export default function SyncPage() {
  const [queue, setQueue] = useState<SyncQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    fetchQueue();
  }, [statusFilter]);

  async function fetchQueue() {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: '50',
        ...(statusFilter && { status: statusFilter }),
      });

      const response = await apiFetch(`/api/inventory/sync-queue?${params}`);
      const data = await response.json();
      if (response.ok) setQueue(data.queue);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const statusConfig: Record<string, { label: string; color: string; icon: React.ElementType }> = {
    pending: { label: '待機中', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
    processing: { label: '処理中', color: 'bg-blue-100 text-blue-700', icon: RefreshCw },
    done: { label: '完了', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    failed: { label: '失敗', color: 'bg-red-100 text-red-700', icon: XCircle },
  };

  const stats = {
    pending: queue.filter(q => q.status === 'pending').length,
    processing: queue.filter(q => q.status === 'processing').length,
    done: queue.filter(q => q.status === 'done').length,
    failed: queue.filter(q => q.status === 'failed').length,
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <RefreshCw size={28} />
            同期設定
          </h1>
          <p className="mt-1" style={{ color: 'var(--text-secondary)' }}>
            在庫同期キューの状態を確認・管理
          </p>
        </div>
        <button onClick={fetchQueue} className="btn-secondary flex items-center gap-2">
          <RefreshCw size={16} />
          更新
        </button>
      </div>

      {/* ステータス統計 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(statusConfig).map(([key, config]) => {
          const Icon = config.icon;
          return (
            <div
              key={key}
              onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
              className={`card p-4 cursor-pointer transition-all ${statusFilter === key ? 'ring-2 ring-blue-600' : 'hover:shadow-md'}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.color}`}>
                  <Icon size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                    {stats[key as keyof typeof stats]}
                  </p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{config.label}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* キュー一覧 */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : queue.length === 0 ? (
          <div className="p-8 text-center" style={{ color: 'var(--text-secondary)' }}>
            同期キューは空です
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>バリアントID</th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>ステータス</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>同期在庫数</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>対象店舗数</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>試行回数</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>作成日時</th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
              {queue.map((item) => {
                const config = statusConfig[item.status];
                return (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <td className="px-4 py-3 font-mono text-sm" style={{ color: 'var(--text-primary)' }}>
                      {item.variantId.substring(0, 8)}...
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${config?.color}`}>
                        {config?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium" style={{ color: 'var(--text-primary)' }}>
                      {item.stockToSync}
                    </td>
                    <td className="px-4 py-3 text-right" style={{ color: 'var(--text-secondary)' }}>
                      {item.targetShopIds?.length || 0}
                    </td>
                    <td className="px-4 py-3 text-right" style={{ color: 'var(--text-secondary)' }}>
                      {item.attempts}
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {new Date(item.createdAt).toLocaleString('ja-JP')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

